// MINHA CONTA — a área que absorveu "configurações".
//
// Sete pontos do T2 pedem esta tela, e o argumento dele é de uma frase:
//
//   T2.99  "quando você abre configurações, eu queria ver se tinha uma coisa mais importante,
//           né, para ter a divisão de minha conta e configurações"
//   T2.100 "em minha conta você entra para você, ok, visualizar seus dados, mas também poder
//           editar. E quando você entra em configurações, que você tem para fazer é editar os
//           dados e embaixo um negócio de versão clássica"
//   T2.101 "pode ser tudo, então minha conta é uma coisa só (...) botar uma divisão de
//           configurações SE TIVER ALGO que precisa colocar. Mas no momento não tem nada"
//   T2.102 "só a parte de versão clássica que inclusive já no rodapé da página também já aparece"
//   T2.105 "configuração para mim tá nada a ver. Tanto o termo quanto essa página existir,
//           porque não tem, ela não tem propósito."
//
// ── A MEDIÇÃO QUE CONFIRMA O T2.101 ─────────────────────────────────────────────────────────
//
// Em 2026-09-12, `/configuracoes` tinha exatamente três seções: "Sua conta" (ver os dados e um
// botão que levava para `/minha-conta`), "Versão clássica" (um link) e "Sessão" (sair). Ou seja:
// **tirando sair da conta, a página não fazia nada que `/minha-conta` não fizesse** — ela era um
// botão para outra página, mais um link que o rodapé já tem.
//
// Então a fusão não perde função nenhuma. Ela junta: ver os dados, editá-los, e sair.
//
// ── A CONDIÇÃO DELE FICA REGISTRADA, PORQUE ELA VAI ENVELHECER ──────────────────────────────
//
// "botar uma divisão de configurações SE TIVER ALGO que precisa colocar. Mas no momento não tem
// nada." A fusão é para agora. Se um dia aparecer configuração de verdade — preferência de tema,
// idioma, notificação —, a divisão volta, e o ponto que a autoriza é este mesmo.
//
// ── O NOME: "MINHA CONTA" (dúvida D3, resolvida com a autonomia de 2026-09-12) ──────────────
//
// Ele reprovou "configurações" — "quando eu vejo configurações eu penso muito assim de ESTRUTURA
// DO SITE" (T2.104) — e não disse o substituto: "Tá, cabe. Depois pensa outra coisa" (T2.105).
//
// Os quatro blocos que não viraram ponto mostram que eles QUASE concluíram: ela propõe "minha
// conta", levanta a objeção (cadastrar instituição ficaria confuso ali), ele desfaz a objeção
// ("você está vinculando só a conta a isso, né?"), e ela aceita — "Ah, pode ser, pode ser. Tá,
// cabe."
//
// Decidi "Minha conta": é a proposta dela com a objeção já respondida por ele, e é a decisão mais
// barata de reverter de todas — um rótulo. Argumento inteiro em `DECISOES-COM-AUTONOMIA.md`.
//
// ── POR QUE ESTE ARQUIVO USA O SCSS DE `settings` ──────────────────────────────────────────
//
// Porque é a mesma tela, com uma seção a menos. Criar `minha-conta.scss` com as mesmas seis
// classes seria a origem da próxima divergência — a lição que o rodapé do XequeMath já pagou
// ("rodapé copiado é rodapé que diverge"). O arquivo de estilo fica onde está e passa a servir a
// esta rota; quando `/configuracoes` deixar de existir de vez, ele se muda de pasta, não de
// conteúdo.

import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { souAdministrador } from "../../admin/o-acesso-ao-painel";
import { paraOndeVoltar } from "../login/para-onde-voltar";
import { Aviso, useAviso } from "../../components/aviso-da-tela";
// T8.21 · as classes de botão da tela de dados, para as duas telas usarem as MESMAS.
import "../sign-up/sign-up.scss";
import Nav from "../../components/nav";
import { useGlobalState } from "../../global-state-context";
import Firebase from "../../api/firebase.class";
import MinhaInstituicao from "../../instituicao/minha-instituicao";
import "../settings/settings.scss";

function MinhaConta() {
  const navigate = useNavigate();
  const local = useLocation();
  const globalState = useGlobalState();
  const [saindo, setSaindo] = useState(false);
  // T17 · O menu do painel de regras só aparece para quem pode mexer. Começa em `false` e não em
  // `null`: um menu que pisca e some para quem não é administrador é pior do que um que nunca
  // apareceu.
  const [souAdmin, setSouAdmin] = useState(false);

  // T8.15 · o `alert()` de "não foi possível sair" era o único jeito desta tela dizer o que houve.
  const { aviso, erro } = useAviso();

  useEffect(() => {
    if (!globalState) return;
    if (!globalState.authUser?.emailVerified) {
      // T8.24 · COM O DESTINO DE VOLTA. Era `navigate("/login")` seco, e sem `voltarPara` o login cai
      // no padrao — "Meus quadrinhos" —, que e exatamente a queixa: *"depois de entrar leva SEMPRE
      // para Meus quadrinhos"*. A guarda do acervo ja fazia certo; estas duas telas nao.
      navigate("/login", {
        replace: true,
        state: paraOndeVoltar(local, !!globalState.authUser),
      });
    }
  }, [globalState, navigate, local]);

  useEffect(() => {
    // A pergunta vai ao banco e pode demorar ou falhar; falhar vira "não", que é a direção segura.
    let vivo = true;
    void souAdministrador().then((v) => {
      if (vivo) setSouAdmin(v);
    });
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    document.title = "Minha conta · HQ-Lab";
    return () => {
      document.title = "HQ-Lab";
    };
  }, []);

  if (!globalState) return <></>;

  const usuario = globalState.appUser;

  async function sair() {
    if (saindo) return;
    setSaindo(true);
    try {
      // Sai PRIMEIRO, navega depois — T1.35. Navegar antes deixava o estado global re-hidratar
      // de uma sessão ainda válida, que é a "conta que não sai".
      await Firebase.logOut();
      navigate("/");
    } catch (err) {
      console.error("[minha-conta] falha ao sair da conta", err);
      erro("Não deu para sair da sua conta agora. Tente de novo em instantes.");
      setSaindo(false);
    }
  }

  return (
    <div className="settings-container">
      <Nav />

      <main className="settings-card">
        <h1>Minha conta</h1>

        {/* T8.15 · a faixa desta tela. O unico aviso que ela dava era o `alert()` de "nao foi
            possivel sair", e ele e importante: se sair falhou, A SESSAO CONTINUA ABERTA — num
            computador de escola que a proxima crianca vai usar. */}
        <Aviso aviso={aviso} />

        <section className="settings-section">
          <h2>Seus dados</h2>
          <dl className="dados-conta">
            <div>
              <dt>Nome</dt>
              <dd>{usuario?.Name() || "—"}</dd>
            </div>
            <div>
              <dt>E-mail</dt>
              <dd>{usuario?.Email() || globalState.authUser?.email || "—"}</dd>
            </div>
            <div>
              <dt>Cidade</dt>
              <dd>{usuario?.City() || "—"}</dd>
            </div>
            {/* A ESCOLA ENTROU NA LISTA, e ela não estava em `/configuracoes`.
                O campo existe no cadastro desde sempre (`sign-up.tsx`), como texto livre e
                opcional — que é exatamente o que o T2.68 descreve: "na conta ele já tem a opção
                de você colocar a escola, não é obrigatório; então o meu mesmo tá vazio". Mostrar
                o que está guardado é o mínimo de uma área chamada "minha conta", e é também o que
                torna visível o vazio de que ele reclamou. */}
            <div>
              <dt>Escola</dt>
              <dd>{usuario?.EducationalInstitution() || "—"}</dd>
            </div>
          </dl>

          <button
            type="button"
            // T8.21 · A MESMA CLASSE DO BOTÃO PRINCIPAL DA TELA DE DADOS.
            //
            // Ele: *"'Minha conta' (card branco, título preto, link azul padrão) não combina com
            // 'Editar meus dados' (tema verde)."* Medido em 27/09: aqui o botão saía com fundo #f8f8f8 e
            // canto de 10 px; a tela seguinte usa #ffd84d e canto de 999 px. Duas telas da mesma jornada,
            // dois desenhos de botão.
            //
            // E este É o botão principal desta seção — o único que age. Deixá-lo secundário invertia a
            // hierarquia, que é o mesmo defeito que o `settings.scss` já tinha corrigido no "Sair".
            className="conta-botao-principal minha-conta-editar"
            onClick={() => navigate("/minha-conta/editar")}
          >
            {/* T8.27 · ESTE BOTAO NAVEGA; o da tela seguinte DESTRAVA. Diziam a mesma frase, e o
                segundo clique parecia o primeiro nao ter funcionado — a queixa e literal sobre isso:
                *"clicar em 'Editar meus dados' em Minha conta e DE NOVO, COM O MESMO TEXTO"*. */}
            Ver e editar meus dados
          </button>
        </section>

        {/* ── A INSTITUIÇÃO, QUE É ONDE O T2.103 A PÕE ────────────────────────────────
            "pode ter coisa aqui, por exemplo, para criar instituição aqui (...) e tem agora as
             coisas do criar instituição, vincular instituição, esse tipo de coisa."

            Vive aqui e não em rota própria porque é ele quem a coloca dentro desta área — e
            porque pedir vínculo é sobre a SUA CONTA, não sobre o seu trabalho. */}
        {usuario && <MinhaInstituicao appUser={usuario} />}

        {/* A SEÇÃO "VERSÃO CLÁSSICA" SAIU DAQUI — T2.102.
            "só a parte de versão clássica que inclusive já no rodapé da página também já aparece
             (...) então também não precisava estar aparecendo em outro campo."

            Ele está certo, e a duplicação tinha história: ali havia um SELETOR DE APARÊNCIA,
            retirado no T1 por decisão dele ("a ideia é que isso seja feito com ROTAS APARTADAS"),
            e o link ficou no lugar do seletor. A seção sobreviveu ao motivo dela.

            O acesso não se perdeu: `components/rodape.tsx` tem "Ver a versão clássica", e o
            rodapé agora existe em toda tela — o que antes não era verdade. */}

        {/* T17 · O PAINEL DAS REGRAS.
            "num painel onde um perfil com autorização administrativa consiga mexer."

            Ele mora dentro de "Minha conta" pelo mesmo motivo que "Minha instituição" mora: é
            onde o operador pôs as coisas que a pessoa faz sobre si e sobre o que ela administra.
            Para quem não é administrador, esta seção simplesmente não existe — e isto é
            conveniência, não segurança: a trava está no banco. */}
        {souAdmin && (
          <section className="settings-section">
            <h2>Regras da plataforma</h2>
            <p className="ajuda">
              As palavras barradas, o que a criança lê quando a barreira fecha, e os números do
              pedido que vai ao desenhista. Salvar vale na hora, sem publicação.
            </p>
            <Link to="/admin/regras">Abrir o painel de regras</Link>
          </section>
        )}
        {souAdmin && (
          <section className="settings-section">
            <h2>Instituições da plataforma</h2>
            <p className="ajuda">
              Ativar a escola pelo primeiro educador, recusar o pedido, suspender ou reativar.
            </p>
            <Link to="/admin/instituicoes">Abrir a moderação das instituições</Link>
          </section>
        )}

        <section className="settings-section">
          <h2>Sessão</h2>
          <p className="ajuda">
            Encerra o acesso neste dispositivo. Seus quadrinhos continuam salvos.
          </p>
          <button
            type="button"
            className="botao-sair"
            onClick={sair}
            disabled={saindo}
          >
            {saindo ? "Saindo…" : "Sair da conta"}
          </button>
        </section>
      </main>
    </div>
  );
}

export default MinhaConta;
